import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface OnboardingModalProps {
  isOpen: boolean;
  userName: string;
  onComplete: () => void;
  onClose?: () => void;
}

type OnboardingStep = 1 | 2;

// --- Tailwind class constants (旧 styled-components) ---
// keyframes は globals.css の `obc-*` へ移設済み。

const OVERLAY =
  'fixed top-0 left-0 right-0 bottom-0 bg-black/60 flex items-center justify-center z-[1100] p-5 animate-[obc-fade-in_0.3s_ease-out]';

const MODAL_CONTAINER =
  'bg-white rounded-2xl w-full max-w-[580px] p-10 shadow-[0_20px_60px_rgba(0,0,0,0.3)] animate-[obc-fade-in-up-20_0.4s_ease-out] relative';

const CLOSE_BUTTON =
  'absolute top-4 right-4 bg-transparent border-0 text-[24px] cursor-pointer text-gray-400 py-1 px-2 rounded transition-all duration-200 ease-[ease] leading-none hover:text-gray-700 hover:bg-gray-100';

const CONTENT_WRAPPER = 'flex flex-col items-center text-center';

const ICON_CONTAINER =
  'w-20 h-20 rounded-full bg-[linear-gradient(135deg,#4f46e5_0%,#7c3aed_100%)] flex items-center justify-center mb-6 text-[36px]';

const TITLE = 'text-[1.5rem] font-bold text-gray-800 m-0 mb-3 leading-[1.4]';

const BUTTON_CONTAINER = 'w-full flex flex-col gap-3';

// Step 2: Success / Banner thumbnails
const SUCCESS_ICON =
  'w-20 h-20 rounded-full bg-[linear-gradient(135deg,#10b981_0%,#059669_100%)] flex items-center justify-center mb-6 text-[36px] text-white';

const BANNER_THUMBNAIL_GRID =
  'flex justify-center gap-4 w-full mb-6 flex-wrap';

const BANNER_THUMBNAIL =
  'aspect-square rounded-xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.1)] bg-gray-100 w-[150px] h-[150px] [&_img]:w-full [&_img]:h-full [&_img]:object-cover';

const BANNER_ITEM_WRAPPER = 'flex flex-col items-center gap-2';

const DOWNLOAD_BUTTON =
  'bg-transparent border border-gray-300 text-gray-500 text-[0.75rem] cursor-pointer py-1 px-3 rounded-md transition-all duration-200 ease-[ease] flex items-center gap-1 hover:text-indigo-600 hover:border-indigo-600 hover:bg-indigo-50';

const BANNER_THUMBNAIL_PLACEHOLDER =
  'w-full h-full flex items-center justify-center bg-[linear-gradient(135deg,#e0e7ff_0%,#c7d2fe_100%)] text-indigo-500 text-[0.75rem] font-semibold';

const SECONDARY_BUTTON =
  'bg-transparent border-0 text-gray-500 text-[0.875rem] cursor-pointer p-2 transition-[color] duration-200 ease-[ease] hover:text-gray-700';

const CTA_BUTTON =
  'w-full py-[14px] px-6 text-[1rem] font-semibold text-white bg-[linear-gradient(135deg,#4f46e5_0%,#7c3aed_100%)] border-0 rounded-lg cursor-pointer transition-all duration-200 ease-[ease] shadow-[0_4px_12px_rgba(79,70,229,0.3)] hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(79,70,229,0.4)] active:translate-y-0';

export const OnboardingModal: React.FC<OnboardingModalProps> = ({
  isOpen,
  userName,
  onComplete,
  onClose,
}) => {
  const navigate = useNavigate();
  const [step, setStep] = useState<OnboardingStep>(1);
  const [bannerUrls, setBannerUrls] = useState<string[]>([]);

  // Reset step when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);
    }
  }, [isOpen]);

  // Load banner URLs from sessionStorage (same-session data).
  // 別セッション用の GET /api/onboarding/banners は廃止した（書き込む画面が無く、常に 401 だった）
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('onboarding_banners');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          const urls = parsed.filter((url: unknown) => typeof url === 'string' && (url as string).length > 0);
          if (urls.length > 0) {
            setBannerUrls(urls);
          }
        }
      }
    } catch {
      // ignore
    }
  }, []);

  const handleStep1Click = useCallback(() => {
    setStep(2);
  }, []);

  const handleDownloadSingle = useCallback(async (url: string, index: number) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `tasky-banner-${index + 1}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, '_blank');
    }
  }, []);

  const handleDownloadBanners = useCallback(() => {
    onComplete();
    navigate('/image-generation');
  }, [onComplete, navigate]);

  const handleClose = useCallback(() => {
    // Clear sessionStorage banner data after viewing (show only once)
    try {
      sessionStorage.removeItem('onboarding_banners');
    } catch {
      // ignore
    }
    if (onClose) {
      onClose();
    }
    onComplete();
  }, [onClose, onComplete]);

  if (!isOpen) {
    return null;
  }

  // Determine display name: hide if empty, null, undefined, or "未設定"
  const isValidName = userName && userName !== '未設定' && userName !== '名称未設定';

  const renderStep1 = () => (
    <div className={CONTENT_WRAPPER}>
      <div className={ICON_CONTAINER}>
        <span role="img" aria-label="sparkles">✨</span>
      </div>
      <h2 className={TITLE}>
        {isValidName ? `${userName}様、` : ''}準備ができました!
      </h2>
      <div className={`${BUTTON_CONTAINER} mt-5`}>
        <button className={CTA_BUTTON} onClick={handleStep1Click}>
          バナーを確認する
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => {
    const hasBanners = bannerUrls.length > 0;

    return (
      <div className={CONTENT_WRAPPER}>
        <div className={SUCCESS_ICON}>
          <span role="img" aria-label="check">✓</span>
        </div>
        <h2 className={TITLE}>
          {hasBanners ? 'あなたのバナーが完成しました!' : 'あなた専用のバナーが完成しています!'}
        </h2>

        <div className={BANNER_THUMBNAIL_GRID}>
          {hasBanners ? (
            bannerUrls.slice(0, 3).map((url, index) => (
              <div className={BANNER_ITEM_WRAPPER} key={index}>
                <div className={BANNER_THUMBNAIL}>
                  <img src={url} alt={`バナー ${index + 1}`} />
                </div>
                <button className={DOWNLOAD_BUTTON} onClick={() => handleDownloadSingle(url, index)}>
                  ダウンロード
                </button>
              </div>
            ))
          ) : (
            [0, 1, 2].map((index) => (
              <div className={BANNER_ITEM_WRAPPER} key={index}>
                <div className={BANNER_THUMBNAIL}>
                  <div className={BANNER_THUMBNAIL_PLACEHOLDER}>
                    Banner
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className={BUTTON_CONTAINER}>
          <button className={CTA_BUTTON} onClick={handleDownloadBanners}>
            {hasBanners ? 'バナー生成画面を開く' : 'バナーをダウンロードする'}
          </button>
          <button className={SECONDARY_BUTTON} onClick={handleClose}>
            閉じる
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={OVERLAY} onClick={(e) => e.stopPropagation()}>
      <div className={MODAL_CONTAINER}>
        {step === 2 && (
          <button className={CLOSE_BUTTON} onClick={handleClose} aria-label="閉じる">
            ×
          </button>
        )}
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
      </div>
    </div>
  );
};

export default OnboardingModal;
